import Image from 'next/image';
import { FC } from 'react';

import { cn } from '@utils/cn';

type LqdLogoProps = {
  title?: string;
  imgUrl?: string;
  size?: 'sm' | 'md' | 'lg';
  square?: boolean;
  renderTitle?: boolean;
  presentation?: 'mark' | 'lockup';
  priority?: boolean;
  tone?: 'auto' | 'dark' | 'light';
  className?: string;
};

const dimensions = {
  sm: { markWidth: 44, markHeight: 32, wordmarkWidth: 64, wordmarkHeight: 32 },
  md: { markWidth: 55, markHeight: 40, wordmarkWidth: 80, wordmarkHeight: 40 },
  lg: { markWidth: 66, markHeight: 48, wordmarkWidth: 96, wordmarkHeight: 48 }
} as const;

const LqdLogo: FC<LqdLogoProps> = ({
  title = 'LQD',
  imgUrl,
  size = 'sm',
  square = false,
  renderTitle = true,
  presentation = renderTitle ? 'lockup' : 'mark',
  priority = false,
  tone = 'auto',
  className
}) => {
  const { markWidth, markHeight, wordmarkWidth, wordmarkHeight } =
    dimensions[size];
  const markSource = imgUrl ?? '/brand/lqd-logomark-trimmed.png';
  const useTextTitle = Boolean(imgUrl) || title !== 'LQD';

  return (
    <span
      aria-label={title}
      className={cn(
        'inline-flex shrink-0 items-center',
        presentation === 'lockup' && 'gap-2',
        className
      )}
    >
      <Image
        src={markSource}
        alt=""
        width={markWidth}
        height={markHeight}
        priority={priority}
        unoptimized={Boolean(imgUrl)}
        className={cn('object-contain', square ? 'rounded-sm' : 'rounded-none')}
      />

      {presentation === 'lockup' && useTextTitle && (
        <span className="self-center whitespace-nowrap text-section-title text-foreground">
          {title}
        </span>
      )}

      {presentation === 'lockup' && !useTextTitle && (
        <span className="relative block" style={{ width: wordmarkWidth }}>
          {(tone === 'auto' || tone === 'light') && (
            <Image
              src="/brand/lqd-wordmark-dark-trimmed.png"
              alt=""
              width={wordmarkWidth}
              height={wordmarkHeight}
              priority={priority}
              className={cn('h-auto w-full', tone === 'auto' && 'dark:hidden')}
            />
          )}
          {(tone === 'auto' || tone === 'dark') && (
            <Image
              src="/brand/lqd-wordmark-light-trimmed.png"
              alt=""
              width={wordmarkWidth}
              height={wordmarkHeight}
              priority={priority}
              className={cn(
                'h-auto w-full',
                tone === 'auto' && 'hidden dark:block'
              )}
            />
          )}
        </span>
      )}
    </span>
  );
};

export default LqdLogo;
